import React, { useState, useEffect } from 'react';
import { AppLayout } from '../components/layout/AppLayout';
import { db } from '../lib/firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where } from 'firebase/firestore';
import { getSupplierSettings } from '../lib/supplierSettings';
import { BrandGradient } from '../components/common/PFILogo';
import { formatDate } from '../utils/dateFormatters';
import { 
  PlusIcon,
  BuildingStorefrontIcon,
  PhoneIcon,
  EnvelopeIcon,
  StarIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  TagIcon
} from '@heroicons/react/24/outline';

export default function SuppliersPage() {
  const [suppliers, setSuppliers] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [supplierSettings, setSupplierSettings] = useState(null);
  const [newSupplier, setNewSupplier] = useState({
    name: '',
    email: '',
    phone: '',
    category: 'upholstery',
    specialties: [],
    address: '',
    website: '',
    notes: '',
    rating: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  // Helper function to format category labels
  const formatCategoryLabel = (category) => {
    return category.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Get categories and specialties from settings or fallback to defaults
  const getCategories = () => {
    return supplierSettings?.categories || [
      'upholstery', 'delivery', 'fabric', 'furniture', 'installation', 'design', 'consultation', 'other'
    ];
  };

  const getSpecialties = () => {
    return supplierSettings?.specialties || [
      'Custom Furniture', 'Fabric Selection', 'Leather Work', 'Antique Restoration', 
      'Modern Design', 'Eco-Friendly Materials', 'Rush Delivery', 'Installation',
      'Consultation', 'Bulk Orders', 'Commercial Projects', 'Residential'
    ];
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load supplier settings first
      const settings = await getSupplierSettings();
      setSupplierSettings(settings);
      
      // Load suppliers
      const suppliersSnapshot = await getDocs(collection(db, 'suppliers'));
      const suppliersData = suppliersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSuppliers(suppliersData);

      // Load all quotes for performance metrics
      const quotesSnapshot = await getDocs(collection(db, 'quotes'));
      const quotesData = quotesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setQuotes(quotesData);

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSupplier = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'suppliers'), {
        ...newSupplier,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      
      setNewSupplier({
        name: '',
        email: '',
        phone: '',
        category: 'upholstery',
        specialties: [],
        address: '',
        website: '',
        notes: '',
        rating: 0
      });
      setShowCreateForm(false);
      loadData();
    } catch (error) {
      console.error('Error creating supplier:', error);
    }
  };

  const handleUpdateSupplier = async (e) => {
    e.preventDefault();
    try {
      await updateDoc(doc(db, 'suppliers', editingSupplier.id), {
        ...editingSupplier,
        updatedAt: new Date()
      });
      
      setEditingSupplier(null);
      loadData();
    } catch (error) {
      console.error('Error updating supplier:', error);
    }
  };

  const handleDeleteSupplier = async (supplierId, supplierName) => {
    if (window.confirm(`Are you sure you want to delete ${supplierName}? This action cannot be undone.`)) {
      try {
        await deleteDoc(doc(db, 'suppliers', supplierId));
        loadData();
      } catch (error) {
        console.error('Error deleting supplier:', error);
      }
    }
  };

  const getSupplierStats = (supplierId) => {
    const supplierQuotes = quotes.filter(q => q.supplierId === supplierId);
    const acceptedQuotes = supplierQuotes.filter(q => q.status === 'accepted');
    const submittedQuotes = supplierQuotes.filter(q => q.status === 'submitted');
    
    const successRate = supplierQuotes.length > 0 ? 
      Math.round((acceptedQuotes.length / supplierQuotes.length) * 100) : 0;
    
    const avgResponseTime = supplierQuotes.length > 0 ? 
      Math.round(supplierQuotes.reduce((sum, q) => {
        if (q.submittedAt && q.invitedAt) {
          const submitted = q.submittedAt.seconds ? new Date(q.submittedAt.seconds * 1000) : new Date(q.submittedAt);
          const invited = q.invitedAt.seconds ? new Date(q.invitedAt.seconds * 1000) : new Date(q.invitedAt);
          return sum + (submitted - invited) / (1000 * 60 * 60 * 24); // days
        }
        return sum;
      }, 0) / submittedQuotes.length) : 0;

    return {
      totalQuotes: supplierQuotes.length,
      acceptedQuotes: acceptedQuotes.length,
      pendingQuotes: supplierQuotes.filter(q => q.status === 'invited').length,
      successRate,
      avgResponseTime
    };
  };


  const filteredSuppliers = suppliers.filter(supplier => {
    const matchesSearch = supplier.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      supplier.specialties?.some(s => s.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || supplier.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading suppliers...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="relative">
        {/* Header */}
        <BrandGradient className="rounded-2xl p-8 mb-8 text-white shadow-xl">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-pink-300" />
                <input
                  type="text"
                  placeholder="Search suppliers..."
                  className="pl-10 pr-4 py-2 bg-white bg-opacity-20 text-gray-900 placeholder-gray-500 border border-white border-opacity-30 rounded-lg focus:ring-2 focus:ring-white focus:border-transparent"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 bg-white bg-opacity-20 text-gray-900 border border-white border-opacity-30 rounded-lg focus:ring-2 focus:ring-white focus:border-transparent"
              >
                <option value="all">All Categories</option>
                {getCategories().map(category => (
                  <option key={category} value={category} className="text-gray-900">
                    {formatCategoryLabel(category)}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={() => setShowCreateForm(true)}
              className="inline-flex items-center px-6 py-3 bg-white text-pink-600 font-semibold rounded-lg hover:bg-pink-50 transition-colors shadow-lg"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              Add Supplier
            </button>
          </div>
          
          <div className="flex items-start">
            <BuildingStorefrontIcon className="w-12 h-12 mr-6 text-white filter drop-shadow-lg" />
            <div className="flex-1">
              <h1 className="text-4xl font-bold mb-3">Supplier Network</h1>
              <p className="text-pink-100 text-xl">
                Manage your supplier database and track performance
              </p>
              <div className="flex items-center space-x-6 text-pink-100 mt-4">
                <div className="flex items-center">
                  <BuildingStorefrontIcon className="w-5 h-5 mr-2" />
                  {suppliers.length} Total Suppliers
                </div>
                <div className="flex items-center">
                  <ChartBarIcon className="w-5 h-5 mr-2" />
                  {quotes.length} Total Quotes
                </div>
              </div>
            </div>
          </div>
        </BrandGradient>

        {/* Create Supplier Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-screen overflow-y-auto">
              <div className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Add New Supplier</h2>
                <form onSubmit={handleCreateSupplier} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
                      <input
                        type="text"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                        value={newSupplier.name}
                        onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })}
                        placeholder="ABC Upholstery Services"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Primary Category *</label>
                      <select
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                        value={newSupplier.category}
                        onChange={(e) => setNewSupplier({ ...newSupplier, category: e.target.value })}
                      >
                        {getCategories().map(category => (
                          <option key={category} value={category}>
                            {formatCategoryLabel(category)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                      <input
                        type="email"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                        value={newSupplier.email}
                        onChange={(e) => setNewSupplier({ ...newSupplier, email: e.target.value })}
                        placeholder="contact@supplier.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                      <input
                        type="tel"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                        value={newSupplier.phone}
                        onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })}
                        placeholder="+61 400 000 000"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                    <input
                      type="url"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      value={newSupplier.website}
                      onChange={(e) => setNewSupplier({ ...newSupplier, website: e.target.value })}
                      placeholder="https://www.supplier.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Specialties</label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                      {getSpecialties().map(specialty => (
                        <label key={specialty} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={newSupplier.specialties.includes(specialty)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setNewSupplier({
                                  ...newSupplier,
                                  specialties: [...newSupplier.specialties, specialty]
                                });
                              } else {
                                setNewSupplier({
                                  ...newSupplier,
                                  specialties: newSupplier.specialties.filter(s => s !== specialty)
                                });
                              }
                            }}
                            className="w-4 h-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                          />
                          <span className="text-sm text-gray-700">{specialty}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                    <textarea
                      rows={2}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      value={newSupplier.address}
                      onChange={(e) => setNewSupplier({ ...newSupplier, address: e.target.value })}
                      placeholder="123 Business Street, Sydney NSW 2000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                    <textarea
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      value={newSupplier.notes}
                      onChange={(e) => setNewSupplier({ ...newSupplier, notes: e.target.value })}
                      placeholder="Any additional notes about this supplier..."
                    />
                  </div>

                  <div className="flex items-center space-x-4 pt-6">
                    <button
                      type="submit"
                      className="px-8 py-3 bg-pink-600 text-white font-semibold rounded-lg hover:bg-pink-700 transition-colors"
                    >
                      Add Supplier
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCreateForm(false)}
                      className="px-8 py-3 bg-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit Supplier Modal */}
        {editingSupplier && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-screen overflow-y-auto">
              <div className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Edit Supplier</h2>
                <form onSubmit={handleUpdateSupplier} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
                      <input
                        type="text"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                        value={editingSupplier.name}
                        onChange={(e) => setEditingSupplier({ ...editingSupplier, name: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Primary Category *</label>
                      <select
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                        value={editingSupplier.category}
                        onChange={(e) => setEditingSupplier({ ...editingSupplier, category: e.target.value })}
                      >
                        {getCategories().map(category => (
                          <option key={category} value={category}>
                            {formatCategoryLabel(category)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                      <input
                        type="email"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                        value={editingSupplier.email}
                        onChange={(e) => setEditingSupplier({ ...editingSupplier, email: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                      <input
                        type="tel"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                        value={editingSupplier.phone || ''}
                        onChange={(e) => setEditingSupplier({ ...editingSupplier, phone: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                    <input
                      type="url"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      value={editingSupplier.website || ''}
                      onChange={(e) => setEditingSupplier({ ...editingSupplier, website: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Specialties</label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                      {getSpecialties().map(specialty => (
                        <label key={specialty} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={(editingSupplier.specialties || []).includes(specialty)}
                            onChange={(e) => {
                              const currentSpecialties = editingSupplier.specialties || [];
                              if (e.target.checked) {
                                setEditingSupplier({
                                  ...editingSupplier,
                                  specialties: [...currentSpecialties, specialty]
                                });
                              } else {
                                setEditingSupplier({
                                  ...editingSupplier,
                                  specialties: currentSpecialties.filter(s => s !== specialty)
                                });
                              }
                            }}
                            className="w-4 h-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                          />
                          <span className="text-sm text-gray-700">{specialty}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                    <textarea
                      rows={2}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      value={editingSupplier.address || ''}
                      onChange={(e) => setEditingSupplier({ ...editingSupplier, address: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                    <textarea
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      value={editingSupplier.notes || ''}
                      onChange={(e) => setEditingSupplier({ ...editingSupplier, notes: e.target.value })}
                    />
                  </div>

                  <div className="flex items-center space-x-4 pt-6">
                    <button
                      type="submit"
                      className="px-8 py-3 bg-pink-600 text-white font-semibold rounded-lg hover:bg-pink-700 transition-colors"
                    >
                      Update Supplier
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingSupplier(null)}
                      className="px-8 py-3 bg-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Suppliers Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredSuppliers.map((supplier) => {
            const stats = getSupplierStats(supplier.id);
            
            return (
              <div key={supplier.id} className="bg-white rounded-2xl shadow-xl border border-pink-100 p-6 pfi-card">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-1">{supplier.name}</h3>
                    <div className="flex items-center mb-2">
                      <TagIcon className="w-4 h-4 mr-2 text-gray-500" />
                      <span className="text-sm text-gray-600 bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                        {formatCategoryLabel(supplier.category || '')}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setEditingSupplier(supplier)}
                      className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteSupplier(supplier.id, supplier.name)}
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-3 mb-4">
                  <div className="flex items-center text-gray-600">
                    <EnvelopeIcon className="w-4 h-4 mr-2" />
                    <a 
                      href={`mailto:${supplier.email}`}
                      className="text-sm text-pink-600 hover:text-pink-700 transition-colors"
                    >
                      {supplier.email}
                    </a>
                  </div>
                  
                  {supplier.phone && (
                    <div className="flex items-center text-gray-600">
                      <PhoneIcon className="w-4 h-4 mr-2" />
                      <a 
                        href={`tel:${supplier.phone}`}
                        className="text-sm text-pink-600 hover:text-pink-700 transition-colors"
                      >
                        {supplier.phone}
                      </a>
                    </div>
                  )}

                  {supplier.website && (
                    <div className="flex items-center text-gray-600">
                      <BuildingStorefrontIcon className="w-4 h-4 mr-2" />
                      <a 
                        href={supplier.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-pink-600 hover:text-pink-700 transition-colors"
                      >
                        Website
                      </a>
                    </div>
                  )}
                </div>

                {supplier.specialties && supplier.specialties.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Specialties</h4>
                    <div className="flex flex-wrap gap-1">
                      {supplier.specialties.slice(0, 3).map(specialty => (
                        <span key={specialty} className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                          {specialty}
                        </span>
                      ))}
                      {supplier.specialties.length > 3 && (
                        <span className="text-xs text-gray-500">+{supplier.specialties.length - 3} more</span>
                      )}
                    </div>
                  </div>
                )}

                {/* Performance Stats */}
                <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Performance</h4>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="text-center">
                      <div className="font-bold text-gray-900">{stats.totalQuotes}</div>
                      <div className="text-gray-500">Total Quotes</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-green-600">{stats.successRate}%</div>
                      <div className="text-gray-500">Success Rate</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-blue-600">{stats.acceptedQuotes}</div>
                      <div className="text-gray-500">Accepted</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-orange-600">{stats.avgResponseTime || 0}d</div>
                      <div className="text-gray-500">Avg Response</div>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>Added: {formatDate(supplier.createdAt)}</span>
                    <div className="flex items-center space-x-1">
                      {stats.totalQuotes > 0 && stats.successRate >= 80 && (
                        <CheckCircleIcon className="w-4 h-4 text-green-500" title="High Performance" />
                      )}
                      {stats.pendingQuotes > 0 && (
                        <ClockIcon className="w-4 h-4 text-orange-500" title="Pending Quotes" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {filteredSuppliers.length === 0 && (
            <div className="col-span-full text-center py-12">
              {searchTerm || selectedCategory !== 'all' ? (
                <>
                  <MagnifyingGlassIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No suppliers found</h3>
                  <p className="text-gray-500 mb-6">No suppliers match your search criteria</p>
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedCategory('all');
                    }}
                    className="text-pink-600 hover:text-pink-700 font-medium"
                  >
                    Clear filters
                  </button>
                </>
              ) : (
                <>
                  <BuildingStorefrontIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No suppliers yet</h3>
                  <p className="text-gray-500 mb-6">Start by adding your first supplier to your network</p>
                  <button
                    onClick={() => setShowCreateForm(true)}
                    className="inline-flex items-center px-6 py-3 bg-pink-600 text-white font-semibold rounded-lg hover:bg-pink-700 transition-colors"
                  >
                    <PlusIcon className="w-5 h-5 mr-2" />
                    Add First Supplier
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
